<script>
	import { onMount } from "svelte";
	import Map from "./map";
	import { platform } from "../../../components/MediaQuerySsr.svelte";

	import centerOfMass from "@turf/center-of-mass";

	import { convertToGeoJSON } from "./mapbox-helpers";
	import { loadJSON, loadMapbox, loadTopojson, getStripes } from "../../../utils";

	import { bezirke, markerData } from "./constants";

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////
	let mapboxMap;
	let tooltip;

	export let darkMode = false;

	// the exported map is only available after the map is loaded
	export let map;
	$: map = mapActivated ? mapboxMap : null;

	export let elemMap;

	export let mapZoom;

	let mapActivated = false;
	export let mapLoaded = false;

	export let showLuftbilder = false;
	const luftbilderId = "luftbilder";

	// ///////////////////////////////////////////////////////////
	// data
	// ///////////////////////////////////////////////////////////

	let labelsBezirke = [];
	let labelsOrtsteile = [];

	let geoBezirke;
	let geoOrtsteile;
	let geoRing;

	// ///////////////////////////////////////////////////////////
	// exported variables and functions
	// ///////////////////////////////////////////////////////////
	export let initialBounds;
	export let maxBounds;
	export let maxZoom;
	export let padding;

	export let initialSelectedPoint;

	export let selectedRegion;
	export let selectedPoint;
	export let hoveredRegion;

	export const scrollToTopOfMap = () => {
		elemMap.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	export const scrollToBottomOfMap = () => {
		const nextPosition = elemMap.getBoundingClientRect().bottom + window.pageYOffset;
		window.scrollTo({ top: nextPosition, behavior: "smooth" });
	};

	// ///////////////////////////////////////////////////////////
	// show/hide luftbilder
	// ///////////////////////////////////////////////////////////
	$: if (mapActivated) {
		if (showLuftbilder) {
			mapboxMap.showLayer(luftbilderId);
		} else {
			mapboxMap.hideLayer(luftbilderId);
		}
		// if (map.map.getLayer(geoData.layerId)) map.map.setPaintProperty(geoData.layerId, "fill-opacity", choroplethOpacity);

		// const idHover = `${geoData.layerId}-hovering`;
		// const idSelected = `${geoData.layerId}-selected`;

		// if (map.map.getLayer(idHover)) map.map.setPaintProperty(idHover, "line-color", highlightingColor);
		// if (map.map.getLayer(idSelected)) map.map.setPaintProperty(idSelected, "line-color", highlightingColor);

		// map.updateChoroplethOpts(geoData.layerId, {
		// 	hoveringBorderColor: highlightingColor,
		// 	selectedBorderColor: highlightingColor,
		// });
	}

	// ///////////////////////////////////////////////////////////
	// remove label for hovered/selected region
	// ///////////////////////////////////////////////////////////
	// $: if (mapActivated) {
	// geoData.labels.forEach((l) => {
	// 	l.opacity =
	// 		(hoveredRegion && l.name === hoveredRegion.name) || (selectedRegion && l.name === selectedRegion.name) ? 0 : 1;
	// });
	// if (map.map.getLayer(geoData.labelLayerID)) map.updateLabels(labelsBriefwahlbezirke, geoData.labelLayerID);
	// }

	// ///////////////////////////////////////////////////////////
	// initialize map
	// ///////////////////////////////////////////////////////////

	// let tooltipHovered = false;

	// const choroplethOpts = {
	// 	geoType: "geojson",
	// 	borderColor: "#FFF",
	// 	borderWidth: 0.3,
	// 	borderOpacity: 0.5,
	// 	opacity: choroplethOpacity,
	// 	dataType: "colors",
	// 	colors: colors,
	// 	data: [0],
	// 	interactive: true,
	// 	zoomOnClick: true,
	// 	zoomLevel: 12.7,
	// 	hoveringBorderColor: highlightingColor,
	// 	hoveringBorderWidth: 3,
	// 	selectedBorderColor: highlightingColor,
	// 	selectedBorderWidth: 3,
	// 	filteredColor: "transparent",
	// 	filteredBorderColor: "transparent",
	// 	choroplethClickCallback: (f, filtered, lngLat) => {
	// 		if (!filtered) selectPoint(lngLat, f);
	// 	},
	// 	backgroundClickCallback: () => {
	// 		clearPoint();
	// 	},
	// 	mousemoveCallback: (f, filtered) => {
	// 		if (isTouch) return;
	// 		hoveredRegion = f.properties;
	// 		addPopup(f);
	// 	},
	// 	mouseleaveCallback: () => {
	// 		setTimeout(() => {
	// 			if (!tooltipHovered) {
	// 				hoveredRegion = undefined;
	// 				removePopup();
	// 				if (selectedRegion) addPopup(selectedRegion);
	// 			}
	// 		});
	// 	},
	// };

	onMount(async () => {
		const promises = [
			// loadJSON(`${PATH}assets/data/map/berlin_bezirke.geo.json`),
			loadJSON(`${PATH}assets/data/map/berlin_bezirke.topo.json`),
			loadJSON(`${PATH}assets/data/map/ring.geo.json`),
			loadJSON(`${PATH}assets/data/map/ortsteile_labels.geo.json`),
			loadTopojson(),
			loadMapbox(),
			// loadDeckgl(),
		];

		let topoBezirke;
		[topoBezirke, geoRing, geoOrtsteile] = await Promise.all(promises);

		geoBezirke = convertToGeoJSON(topoBezirke);

		geoBezirke.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];
			f.properties.name = bezirke[f.properties["BEZ"]];
			labelsBezirke.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoOrtsteile.features.forEach((f) => {
			labelsOrtsteile.push({
				name: `${f.properties.name}`,
				lnglat: f.geometry.coordinates,
			});
		});

		mapboxMap = new Map(elemMap, {
			useFranklin: true,
			initialBounds,
			padding,
			maxZoom: maxZoom,
			maxBounds,
			controls: true,
			controlsVertical: true,
			scale: true,
			popup: false,
			popupShift: false,
			labels: true,
			style: "dark",
			// styleJson: `${PATH}assets/style.json`,
			hideBackgroundMap: false,
		});

		mapboxMap.activated
			.then(() => mapboxMap.loaded)
			.then(() => {
				const REFLAYER = mapboxMap.firstLabelLayer;

				mapboxMap.hideAttribution();

				mapZoom = mapboxMap.map.getZoom();
				mapboxMap.map.on("zoom", () => {
					mapZoom = mapboxMap.map.getZoom();
				});

				mapboxMap.map.loadImage(getStripes("#ccc", 4, "#fff"), (error, image) => {
					mapboxMap.map.addImage("stripes-nodata", image);
				});

				// luftbilder
				mapboxMap.map.addSource(luftbilderId, {
					type: "raster",
					url: "https://interaktiv.tagesspiegel.de/maptiles/luftbilder_2022/tiles.json",
				});

				mapboxMap.map.addLayer(
					{
						id: luftbilderId,
						source: luftbilderId,
						type: "raster",
						paint: {
							"raster-opacity": 0.8, // Adjust between 0 (transparent) and 1 (opaque) to control brightness
							"raster-contrast": -0.3, // Adjust between -1 and 1 to control contrast
						},
					},
					REFLAYER
				);

				// map.addChoropleth(geoData.data, {
				// 	...choroplethOpts,
				// 	id: geoData.layerId,
				// 	identifier: geoData.idKey,
				// 	colors: colors,
				// 	noDataColor: "#fff",
				// 	noDataPattern: "stripes-nodata",
				// 	beforeLayer: REFLAYER,
				// 	zoomPadding: () => {
				// 		return {
				// 			top: 0,
				// 			left: 0,
				// 			bottom: platform === "mobile" ? 150 : 0,
				// 			right: platform === "mobile" ? 0 : 290,
				// 		};
				// 	},
				// });

				// bezirke
				// mapboxMap.addChoropleth(geoBezirke, {
				// 	geoType: "geojson",
				// 	id: "bezirke",
				// 	borderColor: "#000",
				// 	borderWidth: 2,
				// 	interactive: false,
				// 	opacity: 0,
				// 	beforeLayer: REFLAYER,
				// });

				// labels
				const labelsOpts = {
					minZoom: 12.5,
					maxZoom: 14.5,
					fontSize: 12,
					color: "#C1C7D6",
					haloWidth: 1,
					haloColor: "#1D1A3B",
					letterSpacing: 0.015,
					font: "Franklin Gothic Medium",
				};

				mapboxMap.addLabels(labelsBezirke, {
					...labelsOpts,
					id: "labels-bezirke",
					minZoom: 8,
					maxZoom: 10.6,
				});

				mapboxMap.addLabels(labelsOrtsteile, {
					...labelsOpts,
					id: "labels-ortsteile",
					minZoom: 10.6,
					maxZoom: 14,
				});

				showBerlinMarkers();
				showRing();

				mapActivated = true;

				// requestAnimationFrame(() => {
				// move water in front of choropleth
				// mapboxMap.map.moveLayer("water", "bezirke");
				// });

				mapboxMap.map.once("idle", () => {
					mapLoaded = true;

					// preselect a point
					if (initialSelectedPoint) selectPoint(initialSelectedPoint, undefined, true);
				});
			});
	});

	// ///////////////////////////////////////////////////////////
	// map selection
	// ///////////////////////////////////////////////////////////
	let marker = null;
	let popup = null;

	export const removeMarker = () => {
		if (marker) marker.remove();
	};

	export const addMarker = (lon, lat) => {
		if (marker) marker.remove();
		marker = new window.mapboxgl.Marker({
			color: "#92A5E7",
			scale: 0.7,
		})
			.setLngLat([lon, lat])
			.addTo(mapboxMap.map);
	};

	export const removePopup = () => {
		if (popup) popup.remove();
	};

	export const addPopup = (feature, html) => {
		const props = feature.properties ? feature.properties : feature;

		if (popup) popup.remove();

		if (!html) html = `<div class="name">${props.id}</div>`;

		popup = new window.mapboxgl.Popup({
			color: "#1f1f1f",
			scale: 0.7,
			closeButton: false,
			closeOnClick: false,
			closeOnMove: false,
			className: "region-popup",
		})
			.setLngLat([props.centerLng, props.centerLat])
			.setHTML(html)
			.addTo(mapboxMap.map);
	};

	// ///////////////////////////////////////////////////////////
	// markers
	// ///////////////////////////////////////////////////////////

	const berlinMarkers = [];

	function showBerlinMarkers() {
		mapboxMap.activated
			.then(() => mapboxMap.loaded)
			.then(() => {
				markerData.forEach((d) => {
					const melc = document.createElement("div");
					const mel = document.createElement("div");
					melc.appendChild(mel);
					mel.classList.add("marker");
					mel.classList.add(`marker--${d.n}`);
					mel.innerHTML = d.label || "";
					berlinMarkers.push(new window.mapboxgl.Marker(melc).setLngLat([d.lon, d.lat]).addTo(mapboxMap.map));
				});
			});
	}

	function hideBerlinMarkers() {
		mapboxMap.activated
			.then(() => mapboxMap.loaded)
			.then(() => {
				berlinMarkers.forEach((m) => {
					m.remove();
				});
			});
	}

	let ringshown = false;
	let ringshownonce = false;
	let ringmarker;
	function showRing() {
		if (ringshown) return;
		ringshown = true;
		if (!ringshownonce) {
			ringshownonce = true;
			mapboxMap.activated
				.then(() => mapboxMap.loaded)
				.then(() => {
					mapboxMap.map.addSource("sbahnring", {
						type: "geojson",
						data: geoRing,
					});
					mapboxMap.map.addLayer({
						id: "sbahnring1",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#C1C7D6",
							"line-width": 3,
						},
					});
					mapboxMap.map.addLayer({
						id: "sbahnring2",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#1D1A3B ",
							"line-width": 2,
							"line-dasharray": [1, 1],
						},
					});
					const pos = [13.366688444522936, 52.543837986025096];
					const el = document.createElement("div");
					el.className = "ring-marker";
					el.innerHTML = '<div class="ring-marker-text">S-Bahn<br>Ring</div>';
					ringmarker = new window.mapboxgl.Marker(el).setLngLat(pos);
					ringmarker.addTo(mapboxMap.map);
				});
		} else {
			mapboxMap.map.setLayoutProperty("sbahnring1", "visibility", "visible");
			mapboxMap.map.setLayoutProperty("sbahnring2", "visibility", "visible");
			ringmarker.addTo(mapboxMap.map);
		}
	}

	function hideRing() {
		if (!ringshown) return;
		mapboxMap.map.setLayoutProperty("sbahnring1", "visibility", "none");
		mapboxMap.map.setLayoutProperty("sbahnring2", "visibility", "none");
		ringmarker.remove();
		ringshown = false;
	}

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////
	// $: tooltipRegion = hoveredRegion || selectedRegion || undefined;

	// const closeClick = () => clearPoint();

	// function tooltipClick() {
	// 	// clearPoint();
	// }

	// function tooltipMouseover() {
	// 	if (window.innerWidth <= 544) return;
	// 	tooltipHovered = true;
	// 	if (selectedRegion) {
	// 		hoveredRegion = undefined;
	// 		removePopup();
	// 		if (selectedRegion) addPopup(selectedRegion);
	// 	}
	// }

	// function tooltipMouseout() {
	// 	if (window.innerWidth <= 544) return;
	// 	tooltipHovered = false;
	// }
</script>

<div
	class="map-container"
	on:mouseleave={() => {
		// hoveredRegion = undefined;
		// removePopup();
		// if (selectedRegion) addPopup(selectedRegion);
	}}
>
	<div class="map" bind:this={elemMap} class:dark={darkMode} />

	<div
		class="button-luftbilder"
		class:dark={darkMode}
		class:luftbilder={showLuftbilder}
		on:click={() => {
			showLuftbilder = !showLuftbilder;
		}}
	>
		<div class="button-luftbilder-image button-luftbilder-image-on" />
		<div class="button-luftbilder-image button-luftbilder-image-off" />
		<div class="button-luftbilder-text mobile">
			{showLuftbilder ? "Straßen" : "Luftbild"}
		</div>
		<div class="button-luftbilder-text desktop">
			{showLuftbilder ? "Straßenkarte" : "Luftbild"}
		</div>
	</div>
	<div class="map-loading" class:mapLoaded>
		<div class="ts-spinner">
			<div class="ts-double-bounce1" />
			<div class="ts-double-bounce2" />
		</div>
	</div>
	<!-- {#if tooltipRegion}
		{@const hoveredAndSelectedAreSame =
			hoveredRegion && selectedRegion ? hoveredRegion[geoData.idKey] === selectedRegion[geoData.idKey] : false}
		{@const tooltipRegionIsSelected = (!hoveredRegion || hoveredAndSelectedAreSame) && !!selectedRegion}
		<Tooltip
			region={tooltipRegion}
			isSelected={tooltipRegionIsSelected}
			on:click={tooltipClick}
			on:mouseover={tooltipMouseover}
			on:mouseout={tooltipMouseout}
			closeCbk={closeClick}
			animateFly={$platform === "mobile"}
			isTotal={tooltipRegion === undefined}
		/>
	{/if} -->
	<slot name="tooltip" />
</div>

<style lang="scss">
	.map-container {
		position: relative;
		margin-bottom: 0;
		transition: margin 0.3s ease;
	}

	// ///////////////////////////////////////////////////////////
	// MAP ITSELF
	// ///////////////////////////////////////////////////////////
	.map {
		position: relative;
		// height: 740px;
		height: max(740px, 80vh);
		z-index: 0;
	}

	@media (max-width: $breakpoint-mobile-m) {
		.map {
			height: max(680px, 100vh);
		}
	}

	// ///////////////////////////////////////////////////////////
	// MAP MARKERS
	// ///////////////////////////////////////////////////////////

	:global(.marker) {
		pointer-events: none;
		background-position: center bottom;
		background-size: contain;
		background-repeat: no-repeat;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateX(-50%);
		font-weight: $bold;
		font-size: 11px;
		font-family: $ff-sans;
		line-height: 14px;
		color: #fff;
		text-shadow: 1px 1px #1f1f1f, -1px 1px #1f1f1f, -1px -1px #1f1f1f, 1px -1px #1f1f1f, 0 1px #1f1f1f, 0 -1px #1f1f1f,
			1px 0 #1f1f1f, -1px 0 #1f1f1f, 0 0 2px rgba(0, 0, 0, 0.1);
	}

	:global(.marker.marker--fernsehturm) {
		background-image: url(../assets/images/berlin/fernsehturm.svg);
		height: 45px;
		width: 20px;
	}

	:global(.ring-marker-text) {
		position: absolute;
		font-family: $ff-sans;
		white-space: nowrap;
		font-size: 11px;
		color: #c1c7d6;
		font-weight: $bold;
		text-shadow: 1px 1px #1d1a3b, -1px 1px #1d1a3b, 1px -1px #1d1a3b, -1px -1px #1d1a3b;
		transform: translate(-100%, -100%) !important;
		pointer-events: none;
		line-height: 14px;
		text-transform: uppercase;
		letter-spacing: 0.01em;
	}

	:global(.region-popup) {
		pointer-events: none !important;
	}

	:global(.region-popup .mapboxgl-popup-content) {
		border-radius: 4px;
		font-family: $ff-sans;
		text-align: center;
		padding: 7px 9px !important;
		pointer-events: none !important;
	}

	:global(.region-popup .region) {
		font-weight: $bold;
		font-size: 9px;
		line-height: 13px;
		letter-spacing: 0.085em;
		text-transform: uppercase;
		color: #747474;
	}

	:global(.region-popup .name) {
		font-weight: $bold;
		letter-spacing: 0.015em;
		font-size: 13px;
		line-height: 14px;
	}

	:global(.region-popup .bezirk) {
		letter-spacing: 0.015em;
		font-size: 12px;
		line-height: 15px;
	}

	// very large phones
	@media (max-width: $breakpoint-mobile-l) {
		:global(.marker) {
			font-size: 11px;
			line-height: 13px;
		}

		:global(.marker--fernsehturm) {
			height: 36px;
			width: 16px;
		}
	}

	// ///////////////////////////////////////////////////////////
	// LOADING SPINNER
	// ///////////////////////////////////////////////////////////
	.map-loading {
		position: absolute;
		background: #f2f2f2;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		pointer-events: all;
		z-index: 1;
		transition: opacity 0.2s ease;
		opacity: 1;

		&.mapLoaded {
			opacity: 0;
			pointer-events: none;
		}
	}

	.ts-spinner {
		width: 40px;
		height: 40px;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.ts-double-bounce1,
	.ts-double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: #333;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.ts-double-bounce2 {
		animation-delay: -1s;
	}

	@keyframes sk-bounce {
		0%,
		100% {
			transform: scale(0);
			-webkit-transform: scale(0);
		}

		50% {
			transform: scale(1);
			-webkit-transform: scale(1);
		}
	}

	// ///////////////////////////////////////////////////////////
	// LUFTBILDER
	// ///////////////////////////////////////////////////////////
	.button-luftbilder {
		width: 48px;
		height: 48px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 5px rgba(0, 0, 0, 0.25);
		position: absolute;
		top: 10px;
		right: 10px;
		z-index: 10;

		&.dark {
			background: $main-blue;

			.button-luftbilder-text {
				color: #ffffffc3;
				text-shadow: -1px 1px 0 $main-blue, 1px 1px 0 $main-blue, 1px -1px 0 $main-blue, -1px -1px 0 $main-blue;
			}
		}

		&.hidden {
			display: none;
		}
	}

	.button-luftbilder-image {
		position: absolute;
		left: 4px;
		top: 4px;
		width: calc(100% - 8px);
		height: calc(100% - 8px);
		border-radius: 3px;
		background-size: cover;
		background-position: center center;
		overflow: hidden;
	}

	.button-luftbilder-image-on {
		display: none;
		background-image: url(../assets/images/map/icon_streets.svg);
	}

	.button-luftbilder-image-off {
		background-image: url(../assets/images/map/icon_satellite.svg);
		display: block;
	}

	.button-luftbilder.luftbilder {
		.button-luftbilder-image-on {
			display: block;
		}

		.button-luftbilder-image-off {
			display: none;
		}
	}

	.button-luftbilder-text {
		color: #6a6a6a;
		font-weight: $bold;
		font-size: 11px;
		line-height: 12px;
		text-align: center;
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translate(-50%, 5px);
		text-shadow: -1px 1px 0 #fff, 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff;

		&.desktop {
			width: 84px;
		}
	}

	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
		.button-luftbilder-text.mobile {
			display: none;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.button-luftbilder {
			top: 152px !important;
		}
	}

	@media (max-width: $breakpoint-tablet-l) {
		.button-luftbilder {
			width: 38px;
			height: 38px;
		}

		.button-luftbilder-image {
			left: 3px;
			top: 3px;
			width: calc(100% - 6px);
			height: calc(100% - 6px);
		}

		.button-luftbilder-text.desktop {
			display: none;
		}
	}
</style>
